import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import {
  CONSENT_IS_DRAFT,
  CONSENT_PARAGRAPHS,
  CONSENT_TITLE,
  CONSENT_VERSION,
  researchConsentLabel
} from '../../lib/consent';

/**
 * FR-22 — research-data consent, asked on first use (SoW Bagian 16).
 *
 * Two equal buttons, neither pre-selected and neither styled as the "right"
 * answer: consent nudged by design is not freely given. Declining changes
 * nothing about the app, and the screen says so before the choice, not after.
 *
 * Reached by ProtectedRoute's redirect, and again from /profile to change the
 * answer.
 */
export default function ConsentPage() {
  const { profile, user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const answered = profile?.research_consent_version === CONSENT_VERSION;

  const answer = async (agree: boolean) => {
    if (!user) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await dataService.setResearchConsent(user.id, agree);
      // Without the refresh ProtectedRoute still sees the old profile and
      // sends the patient straight back here.
      await refreshProfile();
      navigate(from === '/consent' ? '/' : from, { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Gagal menyimpan jawaban. Periksa koneksi internet.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen min-h-[100dvh] h-full">
      <section className="screen-card" aria-labelledby="consent-title">
        <div id="consent-title" className="section-title">
          {CONSENT_TITLE}
        </div>

        {CONSENT_IS_DRAFT ? (
          <div className="profile-nudge" role="note">
            Teks ini masih draf dan akan diganti dengan teks resmi.
          </div>
        ) : null}

        <div className="consent-text">
          {CONSENT_PARAGRAPHS.map(paragraph => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>

        {answered ? (
          <div className="subtitle" role="status">
            Jawaban saat ini: <strong>{researchConsentLabel(profile)}</strong>
          </div>
        ) : null}

        {error ? <div className="target-band-error">{error}</div> : null}

        <div className="consent-actions">
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => answer(true)}
          >
            Saya setuju
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => answer(false)}
          >
            Saya tidak setuju
          </button>
        </div>
      </section>
    </div>
  );
}
